import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { FaHandshake, FaGlobeAsia, FaUserTie, FaUsers } from 'react-icons/fa';

// Import header image
import headerImage from '../assets/images/9.jpg';

// Import corporate images
import corpImage1 from '../assets/images/10.jpg';
import corpImage2 from '../assets/images/interactive performances/1.jpg';
import corpImage3 from '../assets/images/12.jpg';
import corpImage4 from '../assets/images/interactive performances/2.jpg';

// Import performance images
import perfImage1 from '../assets/images/14.jpg';
// import perfImage2 from '../assets/images/15.jpg';
import perfImage3 from '../assets/images/18.jpg';
import perfImage4 from '../assets/images/interactive performances/1.jpg';

const CorporateEventsPage: React.FC = () => {
  // Event options
  const eventTypes = [
    {
      id: 1,
      title: "Corporate Functions",
      description: "Elevate your corporate events with authentic cultural performances",
      icon: <FaUserTie className="h-8 w-8" />,
      events: [
        "Annual celebrations",
        "Award ceremonies",
        "Team building events",
        "Product launches"
      ]
    },
    {
      id: 2,
      title: "International Conferences",
      description: "Showcase Sri Lankan culture to international attendees and delegates",
      icon: <FaGlobeAsia className="h-8 w-8" />,
      events: [
        "Conference openings",
        "Cultural nights",
        "Gala dinners",
        "Delegate welcoming ceremonies"
      ]
    },
    {
      id: 3,
      title: "Client Appreciation Events",
      description: "Create memorable experiences for your valued clients and partners",
      icon: <FaHandshake className="h-8 w-8" />,
      events: [
        "Client dinners",
        "Appreciation ceremonies",
        "Partner events",
        "End-of-year gatherings"
      ]
    },
    {
      id: 4,
      title: "Themed Events & Festivals",
      description: "Add cultural authenticity to themed events and festivals",
      icon: <FaUsers className="h-8 w-8" />,
      events: [
        "Cultural festivals",
        "Themed corporate parties",
        "Community events",
        "Charity fundraisers"
      ]
    }
  ];

  // Performance options
  const performances = [
    {
      id: 1,
      title: "Traditional Drum Ensembles",
      description: "Energetic rhythms performed by skilled drummers showcasing traditional beats",
      image: perfImage1,
      duration: "6-10 minutes",
    },
  
    {
      id: 3,
      title: "Cultural Fusion Shows",
      description: "Modern adaptations of traditional performances with contemporary elements",
      image: perfImage3,
      duration: "6-10minutes",
    },
    {
      id: 4,
      title: "Interactive Experience",
      description: "Engage your guests with participatory drumming and dance sessions",
      image: perfImage4,
      duration: "6-10 minutes",
    }
  ];

  return (
    <>
      <PageHeader
        title="Corporate Entertainment"
        subtitle="Authentic cultural experiences for your corporate events"
        backgroundImage={headerImage}
      >
        <Link
          to="/contact#booking"
          className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-full text-white bg-orange-600 hover:bg-orange-700 transition-all shadow-lg"
        >
          Book Your Event
        </Link>
      </PageHeader>

      {/* Introduction Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold font-display text-gray-900 sm:text-4xl">
                Corporate Entertainment with <span className="text-orange-600">Cultural Flair</span>
              </h2>
              <p className="mt-6 text-lg text-gray-600">
                Make your corporate event unforgettable with authentic Sri Lankan cultural performances 
                that engage, inspire, and create lasting impressions on your guests, clients, and team members.
              </p>
              <p className="mt-4 text-lg text-gray-600">
                At Rivisara, we specialize in creating customized cultural entertainment solutions for 
                corporate functions, ensuring a perfect blend of professionalism, cultural authenticity, 
                and engaging performances.
              </p>
              
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="bg-orange-50 rounded-lg p-5 text-center">
                  <div className="font-bold text-orange-600 text-xl">200+</div>
                  <div className="text-sm text-gray-600">Corporate Events Annually</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-5 text-center">
                  <div className="font-bold text-orange-600 text-xl">50+</div>
                  <div className="text-sm text-gray-600">International Performances</div>
                </div>
              </div>
              
              <div className="mt-8">
                <Link
                  to="/contact"
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700"
                >
                  Request a Consultation
                  <svg className="ml-2 -mr-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="relative h-48 overflow-hidden rounded-lg shadow-lg transform -rotate-6 hover:rotate-0 transition-transform duration-300">
                    <img
                      src={corpImage1}
                      alt="Corporate drummers performance"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="relative h-64 overflow-hidden rounded-lg shadow-lg transform rotate-3 hover:rotate-0 transition-transform duration-300">
                    <img
                      src={corpImage2}
                      alt="Corporate event entertainment"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="space-y-4 mt-8">
                  <div className="relative h-64 overflow-hidden rounded-lg shadow-lg transform rotate-6 hover:rotate-0 transition-transform duration-300">
                    <img
                      src={corpImage3}
                      alt="Corporate dance performance"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="relative h-48 overflow-hidden rounded-lg shadow-lg transform -rotate-3 hover:rotate-0 transition-transform duration-300">
                    <img
                      src={corpImage4}
                      alt="International conference performance"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Event Types Section */}
      <section className="py-20 bg-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold font-display text-gray-900 sm:text-4xl">
              Perfect for All <span className="text-orange-600">Corporate Events</span>
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
              We provide cultural entertainment solutions for a wide range of corporate functions and events
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {eventTypes.map((type, index) => (
              <motion.div 
                key={type.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all"
              >
                <div className="bg-orange-100 rounded-full p-4 inline-block mb-4">
                  <div className="text-orange-600">
                    {type.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900">{type.title}</h3>
                <p className="mt-2 text-gray-600">{type.description}</p>
                
                <ul className="mt-4 space-y-2">
                  {type.events.map((event, idx) => (
                    <li key={idx} className="flex items-start">
                      <svg className="flex-shrink-0 h-5 w-5 text-orange-500 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="ml-2 text-gray-600">{event}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Performance Options */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold font-display text-gray-900 sm:text-4xl">
              Our Performance <span className="text-orange-600">Options</span>
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
              Customizable cultural performances to match your event theme and schedule
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {performances.map((performance, index) => (
              <motion.div 
                key={performance.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-lg overflow-hidden transform transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="h-64 relative">
                  <img
                    src={performance.image}
                    alt={performance.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-0 right-0 bg-orange-600 text-white py-1 px-3 rounded-bl-lg">
                    {performance.duration}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900">{performance.title}</h3>
                  <p className="mt-2 text-gray-600">{performance.description}</p>
                  <div className="mt-6">
                    <Link
                      to="/contact#booking"
                      className="inline-flex items-center px-4 py-2 border border-orange-600 rounded-md text-orange-600 hover:bg-orange-50"
                    >
                      Request This Performance
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
            className="mt-16 bg-orange-50 rounded-xl p-8 text-center"
          >
            <h3 className="text-2xl font-bold text-gray-900">Looking for something special?</h3>
            <p className="mt-2 text-gray-600 max-w-2xl mx-auto">
              We can create custom performance packages to match your corporate branding, event theme, audience preferences, and schedule requirements.
            </p>
            <div className="mt-6">
              <Link
                to="/contact"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700"
              >
                Get a Custom Quote
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Why Choose Us */}
      <section className="py-20 bg-gray-900 text-white relative">
        <div className="absolute inset-0 bg-pattern-dark opacity-10"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold font-display sm:text-4xl">
              Why Choose <span className="text-orange-400">Rivisara</span>
            </h2>
            <p className="mt-4 text-lg text-gray-300 max-w-3xl mx-auto">
              Professional cultural entertainment tailored specifically for corporate environments
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-lg p-6"
            >
              <div className="bg-orange-600/30 rounded-full p-3 inline-block mb-4">
                <svg className="h-8 w-8 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold">Professional Performers</h3>
              <p className="mt-2 text-gray-300">Our performers are professionally trained, punctual, and experienced in corporate settings with proper etiquette.</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-lg p-6"
            >
              <div className="bg-orange-600/30 rounded-full p-3 inline-block mb-4">
                <svg className="h-8 w-8 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold">Customization Options</h3>
              <p className="mt-2 text-gray-300">We tailor our performances to match your company branding, event theme, and specific requirements.</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-lg p-6"
            >
              <div className="bg-orange-600/30 rounded-full p-3 inline-block mb-4">
                <svg className="h-8 w-8 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold">High-Energy Impact</h3>
              <p className="mt-2 text-gray-300">Our performances create memorable, high-energy moments that leave lasting impressions on your guests and clients.</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-lg p-6"
            >
              <div className="bg-orange-600/30 rounded-full p-3 inline-block mb-4">
                <svg className="h-8 w-8 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold">Punctuality & Reliability</h3>
              <p className="mt-2 text-gray-300">We understand the importance of timing in corporate events and ensure seamless execution every time.</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-lg p-6"
            >
              <div className="bg-orange-600/30 rounded-full p-3 inline-block mb-4">
                <svg className="h-8 w-8 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
                </svg>
              </div>
              <h3 className="text-xl font-bold">Global Experience</h3>
              <p className="mt-2 text-gray-300">We've performed at international conferences and corporate events across Asia, Europe, and North America.</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-lg p-6"
            >
              <div className="bg-orange-600/30 rounded-full p-3 inline-block mb-4">
                <svg className="h-8 w-8 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold">Team Diversity</h3>
              <p className="mt-2 text-gray-300">Our performers come from diverse backgrounds, allowing us to tailor our team composition to your event's needs.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Client Logos */}
      {/* <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl font-bold text-gray-900">Trusted by Leading Companies</h2>
          </motion.div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center opacity-60">
            {/* Replace with actual client logos *
            {Array.from({ length: 6 }).map((_, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex justify-center"
              >
                <div className="h-12 w-32 bg-gray-200 rounded-md flex items-center justify-center">
                  <span className="text-gray-500 font-medium">Client Logo</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}
      
      {/* Call to Action */}
      <section className="py-16 bg-orange-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:flex lg:items-center lg:justify-between">
            <div className="lg:max-w-xl">
              <h2 className="text-3xl font-bold font-display tracking-tight">
                Ready to elevate your corporate event?
              </h2>
              <p className="mt-3 text-lg text-orange-100">
                Contact us for a customized proposal tailored to your specific event needs.
              </p>
            </div>
            <div className="mt-8 lg:mt-0 flex flex-col sm:flex-row sm:space-x-4">
              <Link
                to="/contact#booking"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-orange-600 bg-white hover:bg-orange-50 shadow-lg mb-4 sm:mb-0"
              >
                Request a Quote
              </Link>
              <Link
                to="/gallery"
                className="inline-flex items-center justify-center px-5 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-orange-700 shadow-lg"
              >
                View Past Events
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CorporateEventsPage;
